import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ArrowRightIcon, BoltIcon, ShieldCheckIcon, CommandLineIcon } from '@heroicons/react/24/outline';
import { trainingService, PricingRow, GpuType } from '../services/trainingApi';
import { redirectToCustomerLogin } from '../lib/sso';
import { formatGpuName } from '../lib/format';
import SiteNav from '../components/SiteNav';
import { CodeCard } from '../components/CodeCard';

const SNIPPETS: Record<string, string> = {
  install: `# 1. Install the podstack CLI
curl -fsSL https://github.com/Podstack-ai/podstack-cli-releases/releases/latest/download/install.sh | sh

# 2. Authenticate with an API key (psk_…)
podstack auth login

# 3. SSH into a running GPU instance
podstack gpu instances ssh <instance-id>`,
  workflow: `# Copy your training data up
podstack gpu instances cp ./data <instance-id>:/workspace/data

# Expose a port (e.g. a Jupyter / TensorBoard server)
podstack gpu instances expose <instance-id> 8888

# Tear the instance down when you're done
podstack gpu instances rm <instance-id>`,
};

const Landing = () => {
  const [tab, setTab] = useState<'install' | 'workflow'>('install');
  const { data: pricing } = useQuery({
    queryKey: ['public-pricing'],
    queryFn: () => trainingService.getPublicPricing().then((r) => r.data.data ?? []),
  });
  const { data: gpuTypes } = useQuery({
    queryKey: ['public-gpu-types'],
    queryFn: () => trainingService.getPublicGpuTypes().then((r) => r.data.data ?? []),
  });
  const rows: PricingRow[] = pricing ?? [];
  const types: GpuType[] = gpuTypes ?? [];
  const vramByType = new Map<string, GpuType>(types.map((t) => [t.gpu_type, t]));

  // De-dupe GPU types for the showcase strip.
  const showcase = Array.from(new Map(rows.map((r) => [r.gpu_type, r])).values());

  // GEO/AEO: emit live per-GPU pricing as JSON-LD so JS-rendering answer engines
  // (and rich results) can cite real INR/hour rates. The static AggregateOffer
  // floor in public/index.html remains the crawlable baseline for non-JS bots.
  useEffect(() => {
    const SCRIPT_ID = 'ld-training-pricing';
    const remove = () => document.getElementById(SCRIPT_ID)?.remove();
    if (!pricing || pricing.length === 0) {
      remove();
      return;
    }
    const lowestByType = new Map<string, number>();
    for (const r of pricing) {
      const cur = lowestByType.get(r.gpu_type);
      if (cur === undefined || r.price_per_hour < cur) lowestByType.set(r.gpu_type, r.price_per_hour);
    }
    const offers = Array.from(lowestByType.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([gpuType, price]) => ({
        '@type': 'Offer',
        name: `${formatGpuName(gpuType)} - on-demand GPU, per hour`,
        priceCurrency: 'INR',
        price: price.toFixed(2),
        availability: 'https://schema.org/InStock',
        url: 'https://training.podstack.ai/',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'INR',
          price: price.toFixed(2),
          unitText: 'hour',
        },
      }));
    const prices = Array.from(lowestByType.values());
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Podstack Training Cloud',
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Cloud',
      url: 'https://training.podstack.ai/',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'INR',
        lowPrice: Math.min(...prices).toFixed(2),
        highPrice: Math.max(...prices).toFixed(2),
        offerCount: String(offers.length),
        availability: 'https://schema.org/InStock',
        offers,
      },
    };
    let el = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = SCRIPT_ID;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    return remove;
  }, [pricing]);

  return (
    <div className="min-h-screen bg-ps-page text-ps-text">
      <SiteNav getStartedLabel="Launch a GPU" />

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-28 sm:px-6 sm:pt-32">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="ps-eyebrow">TrainPods · Training studio</span>
            <h1 className="mt-4 text-4xl font-medium leading-[1.08] sm:text-5xl lg:text-[56px]">
              Podstack <span className="text-ps-accent">Training Cloud</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-ps-muted">
              Rent GPUs by the hour, train your models, and connect securely via the podstack CLI. Pay in INR from
              your Podstack wallet.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => redirectToCustomerLogin()} className="ps-btn">
                Launch a GPU <ArrowRightIcon className="h-4 w-4" />
              </button>
              <a href="#pricing" className="ps-btn2">View pricing</a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-ps-muted">
              <span className="flex items-center gap-2"><BoltIcon className="h-4 w-4 text-ps-accent" /> Instant-boot GPUs</span>
              <span className="flex items-center gap-2"><CommandLineIcon className="h-4 w-4 text-ps-accent" /> Connect via CLI</span>
              <span className="flex items-center gap-2"><ShieldCheckIcon className="h-4 w-4 text-ps-accent" /> No browser terminal - SSH keys stay local</span>
            </div>
          </div>
          <div>
            <CodeCard title="podstack - connect" lines={SNIPPETS.install.split('\n')} />
          </div>
        </div>
      </section>

      {/* GPU showcase strip */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {showcase.slice(0, 8).map((r) => {
            const gt = vramByType.get(r.gpu_type);
            return (
              <div key={r.gpu_type} className="ps-card p-4">
                <div className="truncate text-sm font-semibold text-ps-text">{formatGpuName(r.gpu_type)}</div>
                <div className="mt-1 text-xs text-ps-subtle">
                  {gt?.vram_gb ? `${gt.vram_gb} GB VRAM` : 'GPU'}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing table */}
      <section id="pricing" className="mx-auto max-w-7xl scroll-mt-24 px-4 pb-16 sm:px-6">
        <h2 className="mb-6 text-2xl font-medium sm:text-3xl">Pricing</h2>
        <div className="ps-card overflow-hidden">
          {/* Desktop / tablet: compact table, all columns fit without scroll. */}
          <table className="ps-tbl hidden md:table">
            <thead>
              <tr>
                <th>GPU</th>
                <th>VRAM</th>
                <th>Region</th>
                <th>Tier</th>
                <th className="!text-right">Price / hr</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const gt = vramByType.get(r.gpu_type);
                return (
                  <tr key={`${r.gpu_type}:${r.region}:${r.tier}:${i}`}>
                    <td className="font-medium text-ps-text">{formatGpuName(r.gpu_type)}</td>
                    <td className="text-ps-muted">{gt?.vram_gb ? `${gt.vram_gb} GB` : '-'}</td>
                    <td className="text-ps-muted">{r.region}</td>
                    <td className="text-ps-muted">{r.tier}</td>
                    <td className="text-right font-semibold">₹{r.price_per_hour.toFixed(2)}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="!py-8 text-center text-ps-subtle">
                    GPU catalog loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Mobile: one card per row so every field is visible without scroll. */}
          <div className="divide-y divide-ps-raised md:hidden">
            {rows.map((r, i) => {
              const gt = vramByType.get(r.gpu_type);
              return (
                <div key={`${r.gpu_type}:${r.region}:${r.tier}:${i}`} className="flex items-start justify-between gap-3 p-4">
                  <div>
                    <div className="font-medium text-ps-text">{formatGpuName(r.gpu_type)}</div>
                    <div className="mt-1 text-xs text-ps-subtle">
                      {gt?.vram_gb ? `${gt.vram_gb} GB` : '-'} · {r.region} · {r.tier}
                    </div>
                  </div>
                  <div className="whitespace-nowrap text-sm font-semibold text-ps-text">₹{r.price_per_hour.toFixed(2)}<span className="font-normal text-ps-subtle"> /hr</span></div>
                </div>
              );
            })}
            {rows.length === 0 && (
              <div className="p-8 text-center text-[13px] text-ps-subtle">GPU catalog loading…</div>
            )}
          </div>
        </div>
      </section>

      {/* Quickstart */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
        <h2 className="mb-6 text-2xl font-medium sm:text-3xl">Quickstart</h2>
        <div className="ps-card overflow-hidden">
          <div className="flex border-b border-ps-border px-4" role="tablist" aria-label="Quickstart">
            {(['install', 'workflow'] as const).map((l) => (
              <button
                key={l}
                role="tab"
                aria-selected={tab === l}
                onClick={() => setTab(l)}
                className={`ps-tab bg-transparent capitalize ${tab === l ? 'ps-tab-on' : ''}`}
              >
                {l}
              </button>
            ))}
          </div>
          <pre className="m-0 overflow-x-auto bg-ps-chrome p-6 font-mono text-[13px] leading-[1.7] text-gray-300">{SNIPPETS[tab]}</pre>
        </div>
      </section>

      <footer className="border-t border-ps-raised bg-ps-chrome py-10 text-center text-[13px] text-ps-subtle">
        © {new Date().getFullYear()} PodStack - Training Cloud ·{' '}
        <a href="https://podstack.ai" className="ps-link">podstack.ai</a>
      </footer>
    </div>
  );
};

export default Landing;
